/** Whether the user wants results cut into subtitle lines. Strictly necessary
 * storage — a UI preference, nothing to do with the consent banner.
 *
 * Three states, not two: "1" on, "0" off, absent = never asked. The
 * difference matters because the default is no longer the same for everyone —
 * someone who asked for SRT came for subtitles, and their first result should
 * open as subtitles rather than as a transcript they have to re-cut by hand.
 * Storing an explicit "0" is what keeps that from overriding a person who
 * turned it off on purpose.
 */
import type { OutputFormat } from '../types/transcription'

const KEY = 'stt_segment_subtitles'
const FORMAT_KEY = 'stt_format'

export function readSegmentPref(fallback = false): boolean {
  try {
    const saved = localStorage.getItem(KEY)
    if (saved === '1') return true
    if (saved === '0') return false
    return fallback
  } catch {
    return fallback
  }
}

export function writeSegmentPref(on: boolean): void {
  try {
    localStorage.setItem(KEY, on ? '1' : '0')
  } catch {
    // private mode with storage denied — the toggle just won't be remembered
  }
}

/** The default for someone who has never touched the switch: on when the
 * format they asked for is a subtitle file. Plain text is the one output
 * nobody reads as subtitles. */
export function segmentDefaultFor(format: OutputFormat | string | null | undefined): boolean {
  return !!format && format !== 'text'
}

/** Same question where no single result is in view (the history page): the
 * format the upload form is set to is the best statement of intent there is. */
export function segmentDefaultFromSavedFormat(): boolean {
  try {
    return segmentDefaultFor(localStorage.getItem(FORMAT_KEY))
  } catch {
    return false
  }
}
